import React, { useState, useEffect, useRef } from 'react';
import httpHelper from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';
import { Method_TYPE } from 'utils/http-helper';
import DataTable from 'react-data-table-component';
import ReportHeader from './ReportHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { convertToIST } from 'utils/formtaDate';
import {
  useFilteredSortedData,
  ReportTableControls,
  ExportButtons,
} from './reportTableUtils';

const SEARCHABLE_FIELDS = [
  'sectorName',
  'leader1Name',
  'leader1Mobno',
  'leader2Name',
  'leader2Mobno',
];

export default function GroupLeader() {
  const [dataTableData, setDataTableValue] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const printRef = useRef();

  useEffect(() => {
    getGroupLeaderDeatils();
  }, []);

  const getGroupLeaderDeatils = async () => {
    try {
      const data = await httpHelper(URL_CONSTANTS.GET_GROUP_LEADER_INFO, Method_TYPE.GET);

      const formattedTableData = data?.map((group) => ({
        id: group?._id,
        sectorName: group?.sector?.name,
        leader1Name: group?.leader1?.name || '----',
        leader1Mobno: group?.leader1?.mobNoPrimary || '----',
        leader2Name: group?.leader2?.name || '-----',
        leader2Mobno: group?.leader2?.mobNoPrimary || '----',
      }));

      setDataTableValue(formattedTableData || []);
    } catch (err) {
      console.error('Error fetching group leader details:', err);
    }
  };

  const filteredData = useFilteredSortedData(dataTableData, {
    searchTerm,
    searchableFields: SEARCHABLE_FIELDS,
    sortBy: 'sectorName',
    sortDirection: 'asc',
  });

  const getBatchInfo = () => {
    const batchData = JSON.parse(localStorage.getItem('currentBatch'));
    if (batchData) {
      return `${batchData.name}:${convertToIST(batchData.startDate)} - ${convertToIST(batchData.endDate)}`;
    }
    return '';
  };

  const columns = [
    {
      name: 'Sl.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
    },
    {
      name: 'Sector',
      selector: row => row.sectorName,
      sortable: true,
      cell: row => <div data-title="Sector">{row?.sectorName}</div>,
    },
    {
      name: 'Leader1',
      selector: row => row?.leader1Name,
      sortable: true,
      cell: row => <div data-title="Leader1">{row?.leader1Name}</div>,
    },
    {
      name: 'L1 MobNo',
      selector: row => row?.leader1Mobno,
      sortable: true,
      cell: row => <div data-title="Leader11Mob">{row?.leader1Mobno}</div>,
    },
    {
      name: 'Leader2',
      selector: row => row?.leader2Name,
      sortable: true,
      cell: row => <div data-title="Leader2">{row?.leader2Name}</div>,
    },
    {
      name: 'L2 MobNo',
      selector: row => row?.leader2Mobno,
      sortable: true,
      cell: row => <div data-title="Leader2Mob">{row?.leader2Mobno}</div>,
    },
  ];

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write('<html><head><title>Group Leader Report</title>');
    printWindow.document.write('<style>table { width: 100%; border-collapse: collapse; } td, th { border: 1px solid black; padding: 8px; }</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(`<h2>TamilNadu Prasanthi Seva - Group Leader Report</h2><p><strong>${getBatchInfo()}</strong></p>`);
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text('Group Leader Report', 14, 10);
    doc.text(`${getBatchInfo()}`, 14, 18);
    autoTable(doc, {
      startY: 20,
      head: [['Sl.No', 'Sector', 'Leader1', 'L1MobNo', 'Leader2', 'L2MobNo']],
      body: filteredData.map((item, index) => [
        index + 1,
        item.sectorName || '----',
        item.leader1Name || '------',
        item.leader1Mobno || '------',
        item.leader2Name || '------',
        item.leader2Mobno || '------',
      ]),
    });
    doc.save('group_leader_info.pdf');
  };

  const handleDownloadXLS = () => {
    const exportData = filteredData.map((item, index) => ({
      'Sl.No': index + 1,
      Sector: item.sectorName,
      Leader1: item.leader1Name,
      'L1 MobNo': item.leader1Mobno,
      Leader2: item.leader2Name,
      'L2 MobNo': item.leader2Mobno,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Group Leader');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(file, 'group_leader_info.xlsx');
  };

  return (
    <>
      <ReportTableControls
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search sector, leader names, mobile numbers..."
        totalCount={dataTableData.length}
        filteredCount={filteredData.length}
        actions={
          <ExportButtons
            show={dataTableData.length > 0}
            onPrint={handlePrint}
            onPdf={handleDownloadPDF}
            onXls={handleDownloadXLS}
          />
        }
      />

      <ReportHeader header='Group Leader Report' />
      <DataTable
        columns={columns}
        data={filteredData}
        defaultSortFieldId={2}
        striped
        highlightOnHover
        fixedHeader
        fixedHeaderScrollHeight='500px'
        className='reports-table'
        noDataComponent={
          <div className="py-10 text-sm text-slate-500">
            {searchTerm.trim() ? 'No rows match your search' : 'No group leader data available'}
          </div>
        }
      />

      <div ref={printRef} className="hidden">
        <table>
          <thead>
            <tr>
              <th>Sl.No</th>
              <th>Sector</th>
              <th>Leader1</th>
              <th>L1MobNo</th>
              <th>Leader2</th>
              <th>L2MobNo</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.sectorName}</td>
                <td>{item.leader1Name}</td>
                <td>{item.leader1Mobno}</td>
                <td>{item.leader2Name}</td>
                <td>{item.leader2Mobno}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
